import { signInExperiencePreviewMessageSender } from '@logto/schemas';
import { isObject } from '@silverhand/essentials';

type PreviewMessageEvent = Pick<MessageEvent, 'data' | 'source' | 'origin'>;

type PreviewMessageContext = {
  parent: Window;
  self: Window;
  isPreview: boolean;
  preview: unknown;
};

const defaultContext = (): PreviewMessageContext => ({
  parent: window.parent,
  self: window,
  isPreview: new URLSearchParams(window.location.search).getAll('preview').join(',') === 'true',
  preview: isObject(window.logtoSsr) ? window.logtoSsr.vibehubPreview : undefined,
});

/**
 * Validates a `postMessage` event for sign-in experience live preview.
 *
 * Preview config is sent from the admin Console iframe parent. Console and Experience
 * run on different origins (e.g. admin :3002 vs tenant :3001), so checking
 * `event.origin === window.location.origin` would reject legitimate messages.
 *
 * The server injects the exact native Console origins into SSR data. Require
 * that list, a real preview page and the direct parent: product login frames
 * are also allowed by Experience CSP, but cannot set preview configuration.
 */
export const isTrustedPreviewMessage = (
  event: PreviewMessageEvent,
  context: PreviewMessageContext = defaultContext()
): boolean => {
  if (
    !isObject(event.data) ||
    !('sender' in event.data) ||
    event.data.sender !== signInExperiencePreviewMessageSender
  ) {
    return false;
  }

  const { parent, self, isPreview, preview } = context;

  if (
    parent === self ||
    !isPreview ||
    !isObject(preview) ||
    !('revision' in preview) ||
    !('enabled' in preview) ||
    !('adminOrigins' in preview) ||
    preview.revision !== 1 ||
    preview.enabled !== true ||
    !Array.isArray(preview.adminOrigins) ||
    preview.adminOrigins.length === 0 ||
    preview.adminOrigins.length > 16 ||
    event.origin === 'null'
  ) {
    return false;
  }

  return (
    event.source === parent &&
    preview.adminOrigins.every((origin: unknown) => {
      if (typeof origin !== 'string') return false;
      try {
        const url = new URL(origin);
        return (
          ['http:', 'https:'].includes(url.protocol) &&
          !url.username &&
          !url.password &&
          !url.hostname.includes('*') &&
          url.origin === origin
        );
      } catch {
        return false;
      }
    }) &&
    preview.adminOrigins.includes(event.origin)
  );
};
